import React from "react";
import styled from "styled-components";
import { PicoAppDto } from "jadro-pico/apps/PicoAppDto.ts";
import { ITheme } from "../Themes/ThemeTypes";
import { ThemeColorEnum, ThemeIconEnum } from "../Themes/ThemeEnums";
import IconButton from "../Button/IconButton/IconButton";
import AppCard from "../AppCard/AppCard";
import AppTileGrid from "../AppCard/AppTileGrid";
import { picoAppIconMap } from "../AppCard/picoAppIconMap";
import { picoAppLogoMap } from "../AppCard/picoAppLogoMap";
import ResponsivePopover from "../Popover/ResponsivePopover";

export type AppsLauncherVariant = "header" | "floating";

export interface AppsLauncherStrings {
  title: string;
  buttonAriaLabel: string;
  closeAriaLabel: string;
}

const FloatingTrigger = styled.div<{ theme: ITheme }>`
  border-radius: 100%;
  background: rgb(${(props) => props.theme.colors.surface});
  box-shadow: ${(props) => props.theme.base.elevation.level2_shadow};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div<{ theme: ITheme }>`
  color: rgb(${(props) => props.theme.colors.on_surface});
  font-size: ${(props) => props.theme.base.typography.title.medium.font_size}px;
  line-height: ${(props) => props.theme.base.typography.title.medium.line_height}px;
  font-weight: ${(props) => props.theme.base.typography.title.medium.font_weight};
`;

interface AppsLauncherViewProps {
  apps: PicoAppDto[];
  strings: AppsLauncherStrings;
  variant: AppsLauncherVariant;
}

const AppsLauncherView: React.FC<AppsLauncherViewProps> = ({ apps, strings, variant }) => {
  if (apps.length === 0) return null;

  return (
    <ResponsivePopover
      wrapperVariant={variant === "floating" ? "floating" : "anchored"}
      desktopAlign={variant === "floating" ? "left" : "right"}
      surfaceColor={ThemeColorEnum.SURFACE_CONTAINER}
      desktopBorderRadius={28}
      desktopWidth="min(340px, calc(100vw - 32px))"
      renderTrigger={({ toggle }) => {
        const trigger = (
          <IconButton
            icon={ThemeIconEnum.APPS}
            onClick={toggle}
            ariaLabel={strings.buttonAriaLabel}
          />
        );
        return variant === "floating" ? <FloatingTrigger>{trigger}</FloatingTrigger> : trigger;
      }}
    >
      {({ close }) => (
        <>
          <TitleRow>
            <Title>{strings.title}</Title>
            <IconButton
              icon={ThemeIconEnum.CLOSE}
              onClick={close}
              ariaLabel={strings.closeAriaLabel}
            />
          </TitleRow>
          <AppTileGrid columns={3}>
            {apps.map((app) => (
              <AppCard
                key={app.id}
                name={app.name}
                href={app.feUrl}
                icon={picoAppIconMap[app.id]}
                logo={picoAppLogoMap[app.id]}
                variant="tile"
              />
            ))}
          </AppTileGrid>
        </>
      )}
    </ResponsivePopover>
  );
};

export default AppsLauncherView;
